<template>
  <div class="search-resource-detail-game-story-section">
    <input-checkbox
      v-if="isSelectable"
      :id="`game-book-selection-${gameBookId}`"
      :value="gameBookId.toString()"
      :selected-values="selectedValues"
      @change="handleSelected"
    />
    <div class="game-story-details">
      <div class="image-placeholder">
        <EmptyImage v-if="imageEmpty" viewBox="0 0 128 120" class="image" />
        <img v-else class="image" :src="gameBookModel.gameStoryBackgroundImageUrl" />
      </div>
      <div class="info">
        <h5 class="game-title">
          <svg-icon name="Game24" kind="indicative" />
          <span>{{ gameTitle }}</span>
        </h5>
        <div class="game-description">
          <rich-text
            :content="gameDescription"
            :expanded="false"
            :expandable="true"
            truncation-type="secondary"
          />
        </div>
      </div>
    </div>
    <cv-link v-if="showOpenInNewTab" class="view-details" @click="handleViewGamificationDetails">
      <svg-icon class="view-ext" name="ExternalLink24" kind="action" />
    </cv-link>
  </div>
</template>

<script setup lang="ts">
import { useStore } from "vuex";
import { isEmpty } from "@utils/var.check";
import { computed } from "vue";
import { useRouter } from "vue-router";
import InputCheckbox from "@plugins/common/components/InputCheckbox.vue";
import EmptyImage from "@images/placeholder/empty-states/EmptyImage.svg?component";
import RichText from "@plugins/richtext/components/RichText.vue";
import { SEARCH_DOMAIN } from "@modules/search/service/search.constant";
import { RESOURCE_DOMAIN_PATH } from "@plugins/common/constant/resource.constant";
import {
  GameBookModel,
  SearchResourceLesson,
} from "@modules/resource-search/resource-search-types.ts";

interface SearchResource {
  type: string;
  assignmentUuid?: string;
  communityResourceUuid?: string;
  templateUuid?: string;
  admin?: string;
  lesson: SearchResourceLesson;
}

interface Props {
  params: SearchResource;
  isSelectable?: boolean;
  isSelected?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isSelectable: false,
  isSelected: false,
});

const emit = defineEmits<(e: "selected", isSelected: boolean) => void>();

const store = useStore();
const router = useRouter();

const isStudent = computed<boolean>(() => store.getters[`auth/isStudent`]);
const gameBookModel = computed<GameBookModel | undefined>(() => props.params.lesson.gameBookModel);
const gameBookId = computed<number | undefined>(() => gameBookModel.value?.id);
const gameDescription = computed<string | undefined>(() => gameBookModel.value?.description);
const gameTitle = computed<string | undefined>(() => gameBookModel.value?.title);

const imageEmpty = computed<boolean>(
  () =>
    isEmpty(gameBookModel.value?.gameStoryBackgroundImage) ||
    isEmpty(gameBookModel.value?.gameStoryBackgroundImageUrl),
);

const lessonUuid = computed<string>(() => props.params.lesson.uuid);

const showOpenInNewTab = computed<boolean>(() => {
  if (props.params.type === "ASSIGNMENT_LESSON" && !isStudent.value) {
    return false;
  }
  return true;
});

const selectedValues = computed<string[]>(() => {
  return props.isSelected && gameBookId.value ? [gameBookId.value.toString()] : [];
});

const handleSelected = (newSelectedValues: string[]) => {
  const isNowSelected =
    !!gameBookId.value && newSelectedValues.includes(gameBookId.value.toString());
  emit("selected", isNowSelected);
};

const handleViewGamificationDetails = () => {
  let routeName: string;
  let routeParams: Record<string, string>;
  let routeQuery: Record<string, string | boolean> | undefined;

  if (props.params.type === "ASSIGNMENT_LESSON") {
    routeName = isStudent.value ? "AssignmentAttemptCover" : "AssignmentViewCover";
    routeParams = { assignmentUuid: props.params.assignmentUuid as string };
  } else {
    routeName = "EditorCover";
    let resourceUuid = lessonUuid.value;
    let resourceDomain = "";

    switch (props.params.lesson.resourceDomain) {
      case SEARCH_DOMAIN.PERSONAL:
        routeName = isStudent.value ? "PersonalLessonAttemptCover" : routeName;
        resourceDomain = RESOURCE_DOMAIN_PATH.MY_LIBRARY;
        break;
      case SEARCH_DOMAIN.MOE:
        routeName = isStudent.value ? "SelfStudyAttemptCover" : routeName;
        resourceDomain = RESOURCE_DOMAIN_PATH.MOE_LIBRARY;
        resourceUuid = props.params.lesson.moeLibraryUuid as string;
        if (lessonUuid.value) {
          routeQuery = { version: lessonUuid.value };
        }
        break;
      case SEARCH_DOMAIN.COMMUNITY:
        resourceDomain = RESOURCE_DOMAIN_PATH.COMMUNITY_GALLERY;
        resourceUuid = props.params.communityResourceUuid as string;
        break;
      case SEARCH_DOMAIN.ALP_TEMPLATE:
        resourceDomain = RESOURCE_DOMAIN_PATH.ALP_TEMPLATE;
        resourceUuid = props.params.templateUuid as string;
        break;
      case SEARCH_DOMAIN.ASSIGNMENT:
        routeName = isStudent.value ? "StudentGroupResourceAttemptCover" : routeName;
        resourceDomain = RESOURCE_DOMAIN_PATH.CLASS_GROUP_RESOURCE;
        break;
    }

    routeParams = {
      resourceDomain: resourceDomain,
      lessonUuid: resourceUuid,
      mode: "view",
    };

    if (props.params.admin && props.params.admin === "admin") {
      routeParams.admin = "admin";
    }
  }

  const route = router.resolve({
    name: routeName,
    params: routeParams,
    query: { ...routeQuery, openGameSubpage: true },
  });

  window.open(route.href, "_blank");
};
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.search-resource-detail-game-story-section {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: $spacing-03;

  .input-checkbox {
    margin: 0;

    .input-icon {
      // fixed value margin-top to align checkbox to center
      margin-top: 38px;
    }
  }

  .game-story-details {
    display: flex;
    gap: $spacing-05;

    .image-placeholder {
      flex: 0 0 auto;
      width: 120px;
      height: 100px;
      border-radius: 10px;
      background-color: $ui-background;

      .image {
        width: 100%;
        height: 100%;
        border-radius: 10px;
        object-fit: cover;
      }
    }

    .info {
      flex: 1;

      .game-title {
        display: flex;
        align-items: center;
        margin-bottom: $spacing-03;
        // To prevent overlap with view details link
        padding-right: $spacing-06;

        .sls-icon {
          margin-right: $spacing-03;
          min-width: 24px;
        }
      }
    }
  }

  .rich-text {
    .rich-text-actions {
      .bx--link {
        // Override generic styling for link in forms
        // To follow the styling in RichTextCard.vue
        @include carbon--type-style("card-long-01", true);
      }
    }
  }

  .view-details {
    position: absolute;
    right: 0;

    [stroke] {
      stroke: $interactive-01;
    }

    [fill] {
      fill: $interactive-01;
    }
  }
}
</style>
